<script lang="ts">
  import { m } from '$lib/paraglide/messages';
  import { createEventDispatcher, onDestroy, onMount } from 'svelte';
  import cross from '$lib/assets/search/cross.svg';
  import { lockScroll, unlockScroll } from '$lib/utils/scroll';
  import { fade, slide } from 'svelte/transition';
  import { browser } from '$app/environment';

  const dispatch = createEventDispatcher<{ closed: void; mainAction: void; headerAction: void }>();

  let dialog: HTMLDivElement;

  let startX: number;
  let startY: number;
  let direction: 'vertical' | 'horizontal' | null;

  export let tall = false;
  export let title: string = '';
  export let mainActionTitle: string = '';
  export let headerActionTitle: string = '';
  export let threshold: number = 0.3;

  const touch = (e: TouchEvent | MouseEvent) => ('changedTouches' in e ? e.changedTouches[0] : e);

  const deltaX = (e: TouchEvent | MouseEvent, startX: number) => startX - touch(e).clientX;

  const deltaY = (e: TouchEvent | MouseEvent, startY: number) => startY - touch(e).clientY;

  const touchStart = (e: TouchEvent | MouseEvent) => {
    startX = touch(e).clientX;
    startY = touch(e).clientY;
  };

  const touchMove = (e: TouchEvent | MouseEvent) => {
    if (!direction) {
      direction = Math.abs(deltaY(e, startY)) > Math.abs(deltaX(e, startX)) ? 'vertical' : 'horizontal';
    }
  };

  const touchEnd = (e: TouchEvent | MouseEvent) => {
    if (direction === 'vertical') {
      const open = -deltaY(e, startY) / dialog.clientHeight < threshold;
      if (!open) {
        close();
      }
    }
    startY = 0;
    direction = null;
  };

  function headerAction() {
    dispatch('headerAction');
    close();
  }

  function mainAction() {
    dispatch('mainAction');
    close();
  }

  function close() {
    dispatch('closed');
  }

  onMount(() => {
    lockScroll();
  });

  if (browser) {
    onDestroy(() => {
      unlockScroll();
    });
  }
</script>

<div aria-hidden="true" class="backdrop" on:click={close} on:keydown={close} in:fade={{ duration: 200 }} out:fade={{ duration: 200 }}></div>

<div
  bind:this={dialog}
  role="dialog"
  class="root"
  on:touchstart={touchStart}
  on:touchmove={touchMove}
  on:touchend={touchEnd}
  in:slide
  out:slide
>
  <header>
    <button on:click={close} class="close">
      <img src={cross} alt={m['actions.close']()} loading="lazy" />
    </button>
    <div class="title">
      {title}
    </div>
    {#if headerActionTitle}
      <button on:click={headerAction} class="header-action">
        {headerActionTitle}
      </button>
    {/if}
  </header>
  <div class="content" class:tall>
    <slot />
  </div>
  {#if mainActionTitle}
    <button on:click={mainAction} class="main-action">
      {mainActionTitle}
    </button>
  {/if}
</div>

<style>
  header {
    height: 56px;
    color: var(--text-primary);
    margin-bottom: 10px;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
  }

  .title {
    position: absolute;
    left: 16px;
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
  }

  button.close {
    width: 24px;
    height: 24px;
    position: absolute;
    right: 16px;
  }

  button.close img {
    width: 15px;
    height: 15px;
    object-fit: contain;
    aspect-ratio: 1;
  }

  button.header-action {
    margin-right: 16px;
    font-weight: 500;
  }

  button.main-action {
    width: 100%;
    background-color: white;
    height: 52px;
    color: var(--magic-potion);
    font-weight: 700;
    border-top: 1px solid rgba(80, 91, 103, 0.1);
    box-shadow: 0px -1px 1px rgba(0, 0, 0, 0.02);
  }

  .root {
    position: fixed;
    background-color: #ffffff;
    width: 100vw;
    max-height: 80vh;
    overflow: auto;
    left: 0px;
    bottom: 0;
    z-index: 9999999;
    box-shadow: 0px -1px 8px rgba(0, 0, 0, 0.25);
    border-radius: 16px 16px 0px 0px;
    font-size: 14px;
  }

  .content {
    overflow: auto;
    background-color: #fcfcfd;
  }

  .content.tall {
    height: 60vh;
  }

  .backdrop {
    pointer-events: none;
    position: fixed;
    left: 0px;
    top: 0px;
    width: 100vw;
    height: 100%;
    background-color: #212121;
    opacity: 0.5;
    z-index: 9999998;
    pointer-events: all;
  }
</style>
